/**
 * 학년도 클래스 규칙 (supabase/migrations/20261101220000_class_academic_year.sql 과 동일)
 * - 학년도 Y = Y년 3월 1일 ~ (Y+1)년 2월 말일 (이용권 연도와 동일, 한국시간)
 * - 종료일 다음 날부터 기록 중지(읽기·다운로드만), 종료일 + 3개월이 지나면 서버에서 삭제
 */
export const CLASS_ARCHIVE_GRACE_MONTHS = 3;

export type ClassYearStatus = 'active' | 'locked' | 'expired';

type YmdParts = { y: number; m: number; d: number };

function kstTodayParts(now: Date = new Date()): YmdParts {
  const kst = new Date(now.getTime() + 9 * 60 * 60 * 1000);
  return { y: kst.getUTCFullYear(), m: kst.getUTCMonth() + 1, d: kst.getUTCDate() };
}

function ymdKey(p: YmdParts): number {
  return p.y * 10000 + p.m * 100 + p.d;
}

function lastDayOfMonth(y: number, m: number): number {
  return new Date(Date.UTC(y, m, 0)).getUTCDate();
}

export function currentAcademicYear(now: Date = new Date()): number {
  const t = kstTodayParts(now);
  return t.m >= 3 ? t.y : t.y - 1;
}

/** 학년도 종료일((Y+1)년 2월 말일) */
export function academicYearEnd(year: number): YmdParts {
  return { y: year + 1, m: 2, d: lastDayOfMonth(year + 1, 2) };
}

/** 유예 종료일(종료일 + 3개월, 말일 보정 — SQL `date + interval '3 months'` 와 동일) */
export function academicYearGraceEnd(year: number): YmdParts {
  const end = academicYearEnd(year);
  const m = end.m + CLASS_ARCHIVE_GRACE_MONTHS;
  const y = end.y + Math.floor((m - 1) / 12);
  const mm = ((m - 1) % 12) + 1;
  return { y, m: mm, d: Math.min(end.d, lastDayOfMonth(y, mm)) };
}

export function formatYmd(p: YmdParts): string {
  return `${p.y}. ${p.m}. ${p.d}.`;
}

export function classYearStatus(year: number | null | undefined, now: Date = new Date()): ClassYearStatus {
  if (typeof year !== 'number' || !Number.isFinite(year)) return 'active';
  const today = ymdKey(kstTodayParts(now));
  if (today <= ymdKey(academicYearEnd(year))) return 'active';
  if (today <= ymdKey(academicYearGraceEnd(year))) return 'locked';
  return 'expired';
}

/** 클래스 표시용 학년도 라벨: 2026 → "26학년도" */
export function academicYearShortLabel(year: number | null | undefined): string {
  if (typeof year !== 'number' || !Number.isFinite(year)) return '';
  return `${String(year % 100).padStart(2, '0')}학년도`;
}

/** 오늘까지 남은 일수(유예 종료일 기준, 음수면 지남) */
export function daysUntilGraceEnd(year: number, now: Date = new Date()): number {
  const g = academicYearGraceEnd(year);
  const t = kstTodayParts(now);
  return Math.round((Date.UTC(g.y, g.m - 1, g.d) - Date.UTC(t.y, t.m - 1, t.d)) / 86400000);
}

export const CLASS_LOCKED_MESSAGE =
  '지난 학년도 클래스는 기록이 중지되었습니다. 유예 기간 동안 내려받기만 할 수 있습니다.';

export function isClassLockedError(err: unknown): boolean {
  const msg = err instanceof Error ? err.message : String((err as { message?: unknown })?.message ?? err ?? '');
  return /CLASS_LOCKED/.test(msg);
}

/** 날짜(YYYY-MM-DD)가 속한 학년도 (3월~다음 해 2월) */
export function academicYearOfDate(ymd: string): number {
  const [y, m] = ymd.split('-').map(Number);
  return m >= 3 ? y : y - 1;
}

/** 학년도 일반 자료(일일기록·할일·예산) 기록 중지 여부 */
export function isAcademicYearLocked(year: number, now: Date = new Date()): boolean {
  return classYearStatus(year, now) !== 'active';
}

export const YEAR_LOCKED_MESSAGE =
  '지난 학년도 자료는 기록이 중지되었습니다. 보관 기간 동안 내려받기만 할 수 있습니다.';

export function isYearLockedError(err: unknown): boolean {
  const msg = err && typeof err === 'object' && 'message' in err ? String((err as { message: unknown }).message) : String(err ?? '');
  return msg.includes('YEAR_LOCKED');
}

/** YEAR_LOCKED 오류를 한국어 안내로 바꿔 던짐 */
export function mapYearLockedError(err: unknown): unknown {
  if (isYearLockedError(err)) return new Error(YEAR_LOCKED_MESSAGE);
  const msg = err && typeof err === 'object' && 'message' in err ? String((err as { message: unknown }).message) : '';
  if (msg.includes('YEAR_NEEDS_PASS')) return new Error(YEAR_NEEDS_PASS_MESSAGE);
  return err;
}

export const YEAR_NEEDS_PASS_MESSAGE = '다음 학년도 이용권이 있어야 다음 학년도 자료를 기록할 수 있습니다.';

/** 오늘 새로 받는 이용권의 학년도(이용권 연도는 2월 시작 → 2월부터는 다음 학년도 이용권) */
export function passYearForPurchase(now: Date = new Date()): number {
  const kst = new Date(now.getTime() + 9 * 3600 * 1000);
  const y = kst.getUTCFullYear();
  return kst.getUTCMonth() + 1 >= 2 ? y : y - 1;
}

/** 이용권이 덮는 학년도 = 종료일이 속한 학년도 */
export function passYearOfEnd(end: Date): number {
  const y = end.getFullYear();
  return end.getMonth() + 1 >= 3 ? y : y - 1;
}
